/**
 * Shared bio templates for provider profile setup
 * Used by both shop owner and independent provider onboarding
 */

import { ProviderType } from '@/shared/types';

export interface BioVariant {
  short: string;
  standard: string;
  long: string;
}

export interface BioTemplate {
  id: string;
  title: string;
  variants: BioVariant;
  tags?: string[];
}

export const BIO_TEMPLATES: Record<ProviderType, BioTemplate[]> = {
  [ProviderType.BARBER]: [
    {
      id: 'precision',
      title: 'The Precision Specialist',
      tags: ['Modern', 'Fades', 'Detail-Oriented'],
      variants: {
        short:
          'Sharp fades, crisp line-ups, and clean finishes. Detail-driven cuts that keep you looking fresh.',
        standard:
          "Sharp fades, crisp line-ups, and modern styles. I'm detail-driven and focused on clean finishes so you leave looking—and feeling—your best.",
        long: 'I specialize in precision fades, crisp line-ups, and modern styles. With a detail-first approach, every cut is tailored to your head shape and lifestyle so you leave looking—and feeling—your best. Ask about skin fades and enhancements.',
      },
    },
    {
      id: 'classic',
      title: 'The Classic Expert',
      tags: ['Traditional', 'Classic', 'Experience'],
      variants: {
        short:
          'Timeless cuts, hot-towel shaves, and old-school craftsmanship—done right.',
        standard:
          'Timeless cuts and hot-towel shaves with old-school craftsmanship. Classic barbering blended with modern polish.',
        long: 'From side-parts and tapers to hot-towel shaves, I blend traditional barbering with modern polish. Expect steady hands, clean lines, and a relaxing experience every visit.',
      },
    },
    {
      id: 'trendsetter',
      title: 'The Trendsetter',
      tags: ['Trendy', 'Modern', 'Creative'],
      variants: {
        short:
          'Fresh trends, creative designs, and camera-ready finishes. Stand out.',
        standard:
          'I stay on the latest trends—fades, textures, and clean designs—so your cut looks current and camera-ready.',
        long: 'From textured crops and mullets to clean designs, I bring runway and street trends to the chair. Expect modern techniques, product tips, and a look that fits your vibe.',
      },
    },
    {
      id: 'beard',
      title: 'The Beard Master',
      tags: ['Beards', 'Grooming', 'Shaving'],
      variants: {
        short:
          'Beard shaping, fades, and hot-towel shaves. Symmetry and sharp lines.',
        standard:
          'Expert beard shaping and hot-towel shaves. I focus on symmetry, sharp lines, and the right length for your face shape.',
        long: 'From sculpting to conditioning, I tailor your beard to your face shape and lifestyle. Sharp lines, natural transitions, and premium hot-towel shaves for a polished finish.',
      },
    },
    {
      id: 'allrounder',
      title: 'The All-Rounder',
      tags: ['Versatile', 'Full Service'],
      variants: {
        short:
          'From classic cuts to fades and beards—full-service barbering you can trust.',
        standard:
          'Full-service barbering: classic cuts, modern fades, beard work, and styling. I listen first, then deliver.',
        long: 'Versatile barbering for any look—classic, modern, or in-between. I listen to what you want, offer options that fit your hair, and deliver a clean, consistent result.',
      },
    },
    {
      id: 'quick',
      title: 'Quick & Professional',
      tags: ['Fast', 'Efficient', 'Walk-ins'],
      variants: {
        short: 'Quality cuts—efficient and on time. Great for busy schedules.',
        standard:
          'Efficient, professional cuts without the wait. Perfect for tight schedules. Walk-ins welcome.',
        long: "Short on time? I deliver consistent, clean results with minimal wait. Book ahead or walk in—either way, you'll leave sharp and on schedule.",
      },
    },
    {
      id: 'custom',
      title: 'Write Your Own',
      tags: ['Custom'],
      variants: { short: '', standard: '', long: '' },
    },
  ],
  [ProviderType.HAIRSTYLIST]: [
    {
      id: 'colorist',
      title: 'The Color Specialist',
      tags: ['Color', 'Balayage', 'Highlights'],
      variants: {
        short: 'Balayage, highlights, and vivid color. I bring your vision to life with precision color work.',
        standard: 'Specializing in balayage, highlights, and vivid color transformations. Every appointment starts with a consultation to make sure the result is exactly what you envision.',
        long: 'I specialize in balayage, highlights, vivid color, and corrective work. Every appointment begins with a consultation to understand your goals, hair history, and lifestyle so we create a look that works for you long-term. Book a consultation to get started.',
      },
    },
    {
      id: 'stylist',
      title: 'The Versatile Stylist',
      tags: ['Cuts', 'Styling', 'All Hair Types'],
      variants: {
        short: 'Cuts, styling, and treatments for all hair types. Your look, your way.',
        standard: 'From precision cuts to blowouts and treatments, I work with all hair types and textures to create a look that fits your personality and lifestyle.',
        long: 'I offer cuts, blowouts, deep conditioning, and styling for all hair types and textures. Whether you want a fresh new look or to maintain your signature style, I tailor every service to your hair goals. Walk-ins welcome; appointments preferred.',
      },
    },
  ],
  [ProviderType.HAIR_BRAIDER]: [
    {
      id: 'protective-styles',
      title: 'The Protective Styles Specialist',
      tags: ['Box Braids', 'Knotless', 'Protective'],
      variants: {
        short:
          'Box braids, knotless, and protective styles done with care for your hair and scalp.',
        standard:
          'I specialize in box braids, knotless braids, and protective styles. Every install starts with a healthy-hair consultation so the look lasts and your edges stay intact.',
        long: 'Box braids, knotless, twists, and protective styles are my craft. I use lightweight tension, scalp-first prep, and quality hair so your install looks great on day one and on week six. Tell me your length, look, and lifestyle — I will tailor it.',
      },
    },
    {
      id: 'loc-specialist',
      title: 'The Loc Specialist',
      tags: ['Locs', 'Retwists', 'Maintenance'],
      variants: {
        short:
          'Loc starts, retwists, and maintenance. Healthy locs that grow with you.',
        standard:
          'I start, maintain, and style locs of every stage. Whether you want microlocs, sisterlocks-style, or traditional, I focus on healthy roots and clean partings.',
        long: 'From loc starts to retwists, interlocking, and styling, I work with all loc stages and sizes. My focus is scalp health, even partings, and longevity — so your locs mature the way you want. Book a consultation if you are starting your journey.',
      },
    },
    {
      id: 'kids-friendly',
      title: 'Kids & Family Friendly',
      tags: ['Kids', 'Gentle', 'Family'],
      variants: {
        short:
          'Gentle braiding for kids and adults. Patient, fast, and easy on the scalp.',
        standard:
          'I braid for the whole family, including kids. Patient pace, gentle tension, and styles that hold up through play, school, and sports.',
        long: 'Family-friendly braiding for every age. I keep tension gentle, take breaks for little ones, and pick durable styles that last through school weeks and weekend sports. Bring snacks — I bring patience and a great finished look.',
      },
    },
    {
      id: 'creative-styles',
      title: 'The Creative Stylist',
      tags: ['Trendy', 'Designs', 'Custom'],
      variants: {
        short:
          'Custom braid designs, color, and trend styles. Bring inspo — I bring it to life.',
        standard:
          'Trend styles, custom parting, and color blends. I love collaborating on designs that feel uniquely yours, from boho braids to feed-ins to statement parts.',
        long: 'Send me your inspiration and I will make it work for your hair. I do feed-ins, boho braids, fulani, custom parting designs, color blends, and statement looks for events. Every install is built to look intentional and last.',
      },
    },
    {
      id: 'custom',
      title: 'Write Your Own',
      tags: ['Custom'],
      variants: { short: '', standard: '', long: '' },
    },
  ],
  [ProviderType.NAIL_TECHNICIAN]: [
    {
      id: 'nail-artist',
      title: 'The Nail Artist',
      tags: ['Nail Art', 'Gel', 'Creative'],
      variants: {
        short: 'Gel, acrylics, and nail art. Clean work and creative designs that last.',
        standard: 'Specializing in gel extensions, acrylics, and custom nail art. I focus on clean application and lasting results so your nails look fresh for weeks.',
        long: 'I specialize in gel extensions, acrylic sets, dip powder, and custom nail art. Every set is built for durability and style. Book your appointment and let me know your design inspiration — I love bringing creative ideas to life.',
      },
    },
  ],
  [ProviderType.ESTHETICIAN]: [
    {
      id: 'skin-specialist',
      title: 'The Skin Specialist',
      tags: ['Facials', 'Skincare', 'Glow'],
      variants: {
        short: 'Facials, peels, and targeted skincare. Healthy skin starts here.',
        standard: 'Offering facials, chemical peels, and customized skincare treatments. I assess your skin type and concerns to build a treatment plan that delivers real results.',
        long: 'I provide facials, chemical peels, microdermabrasion, and customized skincare treatments. Every session starts with a skin analysis so your treatment targets your specific concerns — whether that is acne, aging, hyperpigmentation, or maintenance. Book a consultation to get started.',
      },
    },
  ],
};

export type BioLengthChoice = 'short' | 'standard' | 'long';
